
import { useState, useEffect } from "react";

import { DISPATCH_VEHICLES, HEAVY_VEHICLES, canServeRides, DECLARED_TO_DISPATCH } from "../lib/dispatchGrid";

export default function Providers({ supabase }) {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [convertTarget, setConvertTarget] = useState(null); // shop in the convert dialog
  const [convertData, setConvertData] = useState(null);     // { counts, blockedReason, declared, dispatch, otherShops }
  const [vehicleType, setVehicleType] = useState("");
  const [converting, setConverting] = useState(false);
  const [approvedDelivery, setApprovedDelivery] = useState(false); // capability grants (5b)
  const [approvedRides, setApprovedRides] = useState(false);

  useEffect(() => { loadShops(); }, []);

  async function loadShops() {
    const { data } = await supabase.from("shops").select("*, profiles(full_name, email), products(id), services(id)").order("created_at", { ascending: false });
    if (data) setShops(data);
    setLoading(false);
  }

  async function toggleShop(shopId, current) {
    await supabase.from("shops").update({ is_active: !current }).eq("id", shopId);
    loadShops();
  }

  async function toggleFeatured(shopId, current) {
    await supabase.from("shops").update({ is_featured: !current }).eq("id", shopId);
    loadShops();
  }

  async function startConvert(shop) {
    setConvertTarget(shop);
    setConvertData(null);
    setVehicleType("");
    setApprovedDelivery(false);
    setApprovedRides(false);

    // Gate on the 2026-07-03 migration: without admin_select_staff_members
    // the staff count silently reads 0 and the safety check is meaningless.
    // The is_official column ships in the same migration, so probe for it.
    const { error: gateErr } = await supabase.from("shops").select("is_official").limit(1);
    if (gateErr) {
      setConvertData({ blockedReason: "Run the 2026-07-03 migration first — the staff safety check is not active yet (" + gateErr.message + ")" });
      return;
    }

    const [products, services, staff, employed, otherShops, declaredRes, dispatchRes] = await Promise.all([
      supabase.from("products").select("id", { count: "exact", head: true }).eq("shop_id", shop.id).is("deleted_at", null),
      supabase.from("services").select("id", { count: "exact", head: true }).eq("shop_id", shop.id),
      supabase.from("staff_members").select("id", { count: "exact", head: true }).eq("shop_id", shop.id).eq("status", "active"),
      supabase.from("drivers").select("id", { count: "exact", head: true }).eq("employer_id", shop.owner_id),
      supabase.from("shops").select("id", { count: "exact", head: true }).eq("owner_id", shop.owner_id).neq("id", shop.id).is("deleted_at", null),
      supabase.from("driver_profiles").select("*").eq("owner_id", shop.owner_id).maybeSingle(),
      supabase.from("drivers").select("*").eq("profile_id", shop.owner_id).maybeSingle(),
    ]);

    // Fail closed: if any safety count errored, block the conversion.
    const failed = [products, services, staff, employed].find(r => r.error);
    if (failed) {
      setConvertData({ blockedReason: "Could not verify shop contents — conversion blocked. " + failed.error.message });
      return;
    }

    const counts = {
      products: products.count || 0,
      services: services.count || 0,
      staff: staff.count || 0,
      employedDrivers: employed.count || 0,
    };
    const declared = declaredRes.data;
    const dispatch = dispatchRes.data;
    setConvertData({ counts, declared, dispatch, otherShops: otherShops.count || 0 });
    const initialVehicle = dispatch?.vehicle_type || DECLARED_TO_DISPATCH[declared?.vehicle_type] || "";
    setVehicleType(initialVehicle);
    setApprovedDelivery(canServeRides(initialVehicle));
    setApprovedRides(canServeRides(initialVehicle));
  }

  async function confirmConvert() {
    const shop = convertTarget;
    if (!vehicleType) { alert("Pick the dispatch vehicle type first."); return; }
    const capsLabel = [approvedDelivery && "Delivery", approvedRides && "Rides"].filter(Boolean).join(" + ");
    const ok = window.confirm(capsLabel
      ? `Convert and approve this rider for: ${capsLabel}?`
      : HEAVY_VEHICLES.includes(vehicleType)
      ? `Convert this ${vehicleType} driver as hauling-only? (Hauling is enabled via the vehicle type; delivery/rides capabilities don't apply.)`
      : "No capabilities selected — the rider cannot receive ANY work until a capability is granted. Convert anyway?");
    if (!ok) return;
    setConverting(true);

    // Rider shops leave the featured row too — home browse still relies on it.
    const { error: shopErr } = await supabase.from("shops")
      .update({ is_rider_only: true, is_featured: false }).eq("id", shop.id);
    if (shopErr) { alert("Conversion failed: " + shopErr.message); setConverting(false); return; }

    const flags = {
      is_verified: shop.verification_status === "approved",
      vehicle_type: vehicleType,
      accepts_delivery: true,
      // accepts must not contradict a granted approval (effective
      // capability = approved AND accepts).
      accepts_rides: approvedRides || vehicleType !== "truck",
      accepts_hauling: vehicleType === "truck",
      approved_delivery: approvedDelivery,
      approved_rides: approvedRides,
    };
    const { error: drvErr } = convertData.dispatch
      ? await supabase.from("drivers").update(flags).eq("profile_id", shop.owner_id)
      : await supabase.from("drivers").insert({ profile_id: shop.owner_id, is_active: false, ...flags });
    if (drvErr) {
      alert("Shop was marked as rider but the driver record failed — fix before they can be dispatched: " + drvErr.message);
      setConverting(false);
      return;
    }

    // Multi-shop owners keep their shop-owner UI.
    let ownerNote = "";
    if (convertData.otherShops === 0) {
      const { error: profErr } = await supabase.from("profiles")
        .update({ is_shop_owner: false }).eq("id", shop.owner_id);
      ownerNote = profErr ? "\n\nWarning: is_shop_owner reset failed: " + profErr.message : "";
    } else {
      ownerNote = `\n\nNote: owner has ${convertData.otherShops} other shop(s), so their shop-owner access was kept.`;
    }

    setConverting(false);
    setConvertTarget(null);
    loadShops();
    alert(`Converted to rider. Capabilities: ${capsLabel || "none"}.${shop.verification_status === "approved" ? " Driver is verified and can go active in the app." : " Driver will be verified when their application is approved."}${ownerNote}`);
  }

  const filtered = shops.filter(s => !search || s.name?.toLowerCase().includes(search.toLowerCase()));
  const blockers = convertData?.counts
    ? Object.entries({ Products: convertData.counts.products, Services: convertData.counts.services, "Active staff": convertData.counts.staff, "Employed drivers": convertData.counts.employedDrivers }).filter(([, n]) => n > 0)
    : [];

  return (
    <div>
      {convertTarget && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => !converting && setConvertTarget(null)}>
          <div style={{ background: "#111", border: "1px solid #333", borderRadius: 16, padding: 28, width: 480, maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: "#fff", marginTop: 0 }}>Convert to Rider</h3>
            <div style={{ color: "#ccc", fontSize: 14, marginBottom: 16 }}>
              <strong style={{ color: "#fff" }}>{convertTarget.name}</strong> — owner {convertTarget.profiles?.full_name || "N/A"}
            </div>

            {!convertData ? (
              <div style={{ color: "#666", padding: 20, textAlign: "center" }}>Checking shop contents…</div>
            ) : convertData.blockedReason ? (
              <div style={{ background: "#e74c3c20", border: "1px solid #e74c3c", borderRadius: 8, padding: 14, color: "#e74c3c", fontSize: 14 }}>
                {convertData.blockedReason}
              </div>
            ) : blockers.length > 0 ? (
              <div>
                <div style={{ background: "#e74c3c20", border: "1px solid #e74c3c", borderRadius: 8, padding: 14, marginBottom: 12 }}>
                  <div style={{ color: "#e74c3c", fontWeight: "700", fontSize: 14, marginBottom: 8 }}>Cannot convert — this is a store-based provider:</div>
                  {blockers.map(([label, n]) => (
                    <div key={label} style={{ color: "#e74c3c", fontSize: 13 }}>• {label}: {n}</div>
                  ))}
                </div>
                <div style={{ color: "#999", fontSize: 13, lineHeight: 1.6 }}>
                  Shops with inventory, services, staff, or their own delivery drivers stay shops — their drivers remain tied to the shop. Conversion is only for provider accounts that are actually individual riders.
                </div>
              </div>
            ) : (
              <div>
                <div style={{ background: "#00A65115", border: "1px solid #00A65140", borderRadius: 8, padding: 12, marginBottom: 14, color: "#00A651", fontSize: 13 }}>
                  ✓ No products, services, staff, or employed drivers — safe to convert.
                </div>
                {convertData.declared && (
                  <div style={{ background: "#1a1a1a", borderRadius: 8, padding: 12, marginBottom: 14, color: "#ccc", fontSize: 13 }}>
                    Declared vehicle: <strong style={{ color: "#fff" }}>{convertData.declared.vehicle_type || "—"}</strong>
                    {[convertData.declared.vehicle_make, convertData.declared.vehicle_model].filter(Boolean).length > 0 && (
                      <> ({[convertData.declared.vehicle_make, convertData.declared.vehicle_model].filter(Boolean).join(" ")})</>
                    )}
                  </div>
                )}
                <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>
                  Dispatch Vehicle Type <span style={{ color: "#e74c3c" }}>* required</span>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  {DISPATCH_VEHICLES.map(v => (
                    <button key={v}
                      style={{ background: vehicleType === v ? "#9b59b6" : "#1a1a1a", color: vehicleType === v ? "#fff" : "#999", border: vehicleType === v ? "1px solid #9b59b6" : "1px solid #333", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: "600", cursor: "pointer", textTransform: "capitalize" }}
                      onClick={() => { setVehicleType(v); setApprovedDelivery(canServeRides(v)); setApprovedRides(canServeRides(v)); }}>
                      {v}
                    </button>
                  ))}
                </div>
                <div style={{ color: "#666", fontSize: 12, marginBottom: 12 }}>
                  Van/Truck ⇒ hauling + delivery (no rides). Converting sets is_rider_only, removes Featured, {convertData.otherShops === 0 ? "and switches the owner to the rider UI." : "but keeps the owner's shop-owner access (they own other shops)."}
                </div>
                <div style={{ color: "#666", fontSize: 11, textTransform: "uppercase", marginBottom: 8 }}>
                  Capabilities to Approve
                </div>
                {[
                  { label: "Delivery (incl. marketplace orders)", value: approvedDelivery, set: setApprovedDelivery, disabled: false },
                  { label: "Rides (passenger transport)", value: approvedRides, set: setApprovedRides, disabled: HEAVY_VEHICLES.includes(vehicleType) },
                ].map(c => (
                  <label key={c.label} style={{ display: "flex", alignItems: "center", gap: 8, color: c.disabled ? "#555" : "#ccc", fontSize: 13, marginBottom: 6, cursor: c.disabled ? "not-allowed" : "pointer" }}>
                    <input type="checkbox" checked={c.value} disabled={c.disabled} onChange={e => c.set(e.target.checked)} />
                    {c.label}{c.disabled ? " — heavy vehicles can't take passengers" : ""}
                  </label>
                ))}
                {HEAVY_VEHICLES.includes(vehicleType) && (
                  <div style={{ color: "#f39c12", fontSize: 12, marginBottom: 6 }}>
                    Heavy vehicle: hauling is enabled via the vehicle type — leaving delivery/rides unchecked is expected, not an error.
                  </div>
                )}
                <div style={{ color: "#666", fontSize: 12, marginBottom: 16 }}>
                  Defaults follow the vehicle; confirm before converting.
                </div>
                <button
                  style={{ width: "100%", background: vehicleType ? "#9b59b6" : "#333", color: vehicleType ? "#fff" : "#777", border: "none", borderRadius: 8, padding: "12px", fontSize: 14, fontWeight: "600", cursor: "pointer" }}
                  disabled={converting || !vehicleType}
                  onClick={confirmConvert}>
                  {converting ? "Converting…" : "🏍 Convert to Rider"}
                </button>
              </div>
            )}

            <button style={{ width: "100%", background: "none", border: "none", color: "#666", padding: 12, marginTop: 8, cursor: "pointer", fontSize: 14 }} onClick={() => !converting && setConvertTarget(null)}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center" }}>
        <input style={{ background: "#111", border: "1px solid #333", borderRadius: 8, padding: "10px 16px", color: "#fff", fontSize: 14, width: 320 }} placeholder="Search shops..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ color: "#666", fontSize: 14 }}>{filtered.length} shops</span>
      </div>
      {loading ? <div style={{ color: "#666", textAlign: "center", padding: 40 }}>Loading providers...</div> : (
        <div style={{ background: "#111", border: "1px solid #222", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{["Shop Name", "Owner", "Category", "Products", "Services", "Rating", "Featured", "Status", "Actions"].map(h => <th key={h} style={{ color: "#666", fontSize: 12, fontWeight: "600", textAlign: "left", padding: "12px 16px", borderBottom: "1px solid #222", textTransform: "uppercase", background: "#0a0a0a" }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map(shop => (
                <tr key={shop.id} style={{ borderBottom: "1px solid #1a1a1a" }}>
                  <td style={{ color: "#fff", fontSize: 14, padding: "12px 16px", fontWeight: "600" }}>{shop.name}</td>
                  <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>
                    <div>{shop.profiles?.full_name || "N/A"}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>{shop.profiles?.email || ""}</div>
                  </td>
                  <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>{shop.category || "N/A"}</td>
                  <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>{shop.products?.length || 0}</td>
                  <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>{shop.services?.length || 0}</td>
                  <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>⭐ {shop.rating || "0"}</td>
                  <td style={{ fontSize: 14, padding: "12px 16px" }}>
                    <button style={{ border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: "600", cursor: "pointer", background: shop.is_featured ? "#f39c1220" : "#33333340", color: shop.is_featured ? "#f39c12" : "#666" }} onClick={() => toggleFeatured(shop.id, shop.is_featured)}>{shop.is_featured ? "⭐ Featured" : "Add to Featured"}</button>
                  </td>
                  <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px" }}>
                    <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: "600", background: shop.is_active ? "#00A65120" : "#e74c3c20", color: shop.is_active ? "#00A651" : "#e74c3c" }}>{shop.is_active ? "Active" : "Inactive"}</span>
                  </td>
                  <td style={{ color: "#ccc", fontSize: 14, padding: "12px 16px", whiteSpace: "nowrap" }}>
                    <button style={{ border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: "600", cursor: "pointer", background: shop.is_active ? "#e74c3c20" : "#00A65120", color: shop.is_active ? "#e74c3c" : "#00A651" }} onClick={() => toggleShop(shop.id, shop.is_active)}>{shop.is_active ? "Deactivate" : "Activate"}</button>
                    {shop.is_rider_only ? (
                      <span style={{ marginLeft: 8, padding: "5px 12px", borderRadius: 6, fontSize: 12, fontWeight: "600", background: "#9b59b620", color: "#9b59b6" }}>🏍 Rider</span>
                    ) : (
                      <button style={{ marginLeft: 8, border: "1px solid #9b59b6", borderRadius: 6, padding: "4px 12px", fontSize: 12, fontWeight: "600", cursor: "pointer", background: "none", color: "#9b59b6" }} onClick={() => startConvert(shop)}>→ Rider</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
